import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { money, moneyCompact, num } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Plus, Trash2, FolderPlus, Layers, GripVertical, ChevronDown, ChevronRight, TrendingUp,
  Sparkles, X, Boxes, Search, Wrench, Pencil, Users, FileText, Truck, AlertTriangle, Info, GripHorizontal, Rows3, Copy,
  FolderTree, Eye, EyeOff,
} from "lucide-react";
import { sectionize, tint, DEFAULT_SUBGROUP_COLOR } from "@/lib/subgroups";
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { EquipmentPanel } from "./EquipmentPanel";
import { DraggablePanel, DragHandle } from "@/components/ui/draggable-panel";
import { LaborHoursInput } from "@/lib/laborUnit";
import { CostCodeSelect, CostCodeCombobox } from "@/components/CostCodeSelect";
import { DISCIPLINES } from "@/lib/disciplines";
import { COST_COLORS } from "@/lib/costColors";
import { AssemblyDrilldown, treePerUnit, cleanTreeNodes } from "@/components/takeoff/AssemblyDrilldown";
import { ProvenanceChip, ProvenanceFields } from "@/components/ProvenanceChip";
import { LineKindFields, KINDS as KIND_LIST } from "@/components/KindSelect";
import { LineStepRows } from "@/components/LineStepRows";
import { useAuth } from "@/context/AuthContext";
const KIND_LABELS = Object.fromEntries(KIND_LIST);

let seq = 0;
const nid = () => `ln_${Date.now()}_${seq++}`;
const eff = (l) => Math.round((Number(l.base_quantity) || 0) * (Number(l.multiplier) || 1) * (1 + (Number(l.waste_pct) || 0) / 100) * 100) / 100;
const UOM_OF = { count: "ea", linear: "lf", area: "sf" };
// [drag] line-item cost-code uom qty total-qty amount actions
const COLS = "22px minmax(0,2.3fr) 118px 52px 92px 78px 72px minmax(0,1.1fr) 80px";

// Per-unit labor hours + material derived from an assembly's components.
function perUnitFromAssembly(a) {
  let mh = 0, mat = 0;
  (a.components || []).forEach((c) => {
    const q = (Number(c.qty) || 0) * (Number(c.multiplier) || 1) * (1 + (Number(c.waste_pct) || 0) / 100);
    mh += (Number(c.man_hours) || 0) * q;
    mat += (Number(c.material_cost) || 0) * q;
  });
  const per = (Number(a.per_unit) || 1) || 1;
  return { unit_labor_hours: +(mh / per).toFixed(4), unit_material_cost: +(mat / per).toFixed(4) };
}

// Index-aware freeze of an assembly onto a bid line: material = today's index-adjusted price,
// plus per-component baselines so a revision can reprice proportionally.
function assemblyCommodity(a, idxMap) {
  const per = (Number(a.per_unit) || 1) || 1;
  let mh = 0, matNow = 0, fixed = 0, baseTotal = 0;
  const comps = [];
  (a.components || []).forEach((c) => {
    const qf = (Number(c.qty) || 0) * (Number(c.multiplier) || 1) * (1 + (Number(c.waste_pct) || 0) / 100);
    mh += (Number(c.man_hours) || 0) * qf;
    const baseMat = (Number(c.material_cost) || 0) * qf / per;
    baseTotal += baseMat;
    const iid = c.commodity_index_id;
    const idx = iid ? idxMap[iid] : null;
    const bp = Number(c.commodity_baseline_price) || 0;
    const cur = Number(idx?.current_price) || 0;
    if (iid && idx && bp > 0 && cur > 0) { comps.push({ index_id: iid, baseline_price: bp, base_contrib: +baseMat.toFixed(6) }); matNow += baseMat * cur / bp; }
    else { fixed += baseMat; matNow += baseMat; }
  });
  return {
    unit_labor_hours: +(mh / per).toFixed(4),
    unit_material_cost: +matNow.toFixed(4),
    commodity_fixed_material: +fixed.toFixed(4),
    commodity_base_material: +baseTotal.toFixed(4),
    commodity_components: comps,
  };
}

// ── Searchable assembly dropdown (plain React so it works anywhere) ──
function AssemblyPicker({ assemblies, value, onPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);
  const current = assemblies.find((a) => a.assembly_id === value);
  const s = q.trim().toLowerCase();
  const shown = s
    ? assemblies.filter((a) => (a.name || "").toLowerCase().includes(s) || (a.cost_code || "").toLowerCase().includes(s) || (a.category || "").toLowerCase().includes(s))
    : assemblies;
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="asm-picker-trigger"
        className="w-full flex items-center gap-2 text-left text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white hover:border-[#003E87]/40 transition-colors">
        <Boxes className="w-4 h-4 text-[#003E87] shrink-0" />
        <span className={`truncate flex-1 ${current ? "text-slate-800" : "text-slate-400"}`}>{current ? current.name : "Pick a reusable assembly…"}</span>
        {current?.cost_code && <span className="text-[11px] font-mono text-[#003E87] bg-[#003E87]/10 rounded px-1.5 py-0.5 shrink-0">{current.cost_code}</span>}
        <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden" data-testid="asm-picker-menu">
          <div className="flex items-center gap-2 px-2.5 py-2 border-b border-slate-100">
            <Search className="w-3.5 h-3.5 text-slate-400" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search assemblies…"
              className="flex-1 text-xs outline-none" data-testid="asm-picker-search" />
          </div>
          <div className="max-h-64 overflow-auto py-1">
            <button type="button" onClick={() => { onPick(null); setOpen(false); }} data-testid="asm-picker-none"
              className="w-full text-left px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-50">— Custom line (no assembly) —</button>
            {shown.map((a) => (
              <button key={a.assembly_id} type="button" onClick={() => { onPick(a); setOpen(false); setQ(""); }} data-testid={`asm-pick-${a.assembly_id}`}
                className="w-full text-left px-3 py-1.5 hover:bg-[#003E87]/5 flex items-center justify-between gap-2">
                <span className="text-xs text-slate-800 truncate">{a.name}</span>
                <span className="flex items-center gap-1.5 shrink-0">
                  {a.category && <span className="text-[11px] text-slate-400">{a.category}</span>}
                  {a.cost_code && <span className="text-[11px] font-mono text-[#003E87]">{a.cost_code}</span>}
                </span>
              </button>
            ))}
            {shown.length === 0 && <div className="px-3 py-3 text-xs text-slate-400 text-center">No assemblies match “{q}”.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// Procore-style estimating spreadsheet: parent scopes (groups) -> line items.
export default function EstimatingGrid({ bid, computed, taskTypes = [], updateBid, readOnly, pendingMeasure, clearPending, reload }) {
  const diff = bid.last_import_diff || null;
  const [liveDiff, setLiveDiff] = useState({ added: new Set(diff?.added || []), changed: new Set(diff?.changed || []) });
  useEffect(() => { setLiveDiff({ added: new Set(diff?.added || []), changed: new Set(diff?.changed || []) }); }, [bid.last_import_diff]); // eslint-disable-line
  const addedSet = liveDiff.added;
  const changedSet = liveDiff.changed;
  const fadeHighlight = (id) => setLiveDiff((d) => {
    if (!d.added.has(id) && !d.changed.has(id)) return d;
    const a = new Set(d.added); a.delete(id); const c = new Set(d.changed); c.delete(id);
    return { added: a, changed: c };
  });

  const clearHighlights = async () => {
    try { await api.post(`/bids/${bid.bid_id}/clear-import-diff`); reload && reload(); }
    catch (e) { console.error("clear-import-diff failed", e); }
  };

  const [lines, setLines] = useState(() => (bid.scope_lines || []).map((l) => ({
    ...l,
    line_id: l.line_id || nid(), group: l.group || "General", subgroup: l.subgroup || null,
    cost_code: l.cost_code || l.budget_code || "",
    task_type_id: l.task_type_id || "", name: l.name || "", unit: l.unit || "",
    assembly_id: l.assembly_id || l.takeoff?.assembly_id || null,
    base_quantity: l.base_quantity ?? l.quantity ?? 0, multiplier: l.multiplier ?? 1, waste_pct: l.waste_pct ?? 0,
    quantity: l.quantity ?? 0, source: l.source, markup_id: l.markup_id, inputs: l.inputs, takeoff: l.takeoff, is_subcontract: l.is_subcontract || false,
    difficulty: l.difficulty ?? 1, labor_rate: l.labor_rate, unit_labor_cost: l.unit_labor_cost, unit_labor_hours: l.unit_labor_hours, unit_material_cost: l.unit_material_cost, unit_equipment_cost: l.unit_equipment_cost, unit_travel_cost: l.unit_travel_cost, unit_sub_cost: l.unit_sub_cost,
  })));
  const [codes, setCodes] = useState([]);
  const [assemblies, setAssemblies] = useState([]);
  const [crewLib, setCrewLib] = useState([]);
  const [blendedRate, setBlendedRate] = useState(0);
  const [scopeGroups, setScopeGroups] = useState(bid.scope_groups || []);
  const [scopeSubgroups, setScopeSubgroups] = useState(bid.scope_subgroups || []);
  const [crewPopup, setCrewPopup] = useState(null);
  const toggleCrewPopup = (g) => setCrewPopup((c) => (c === g ? null : g));
  const [equipHintOff, setEquipHintOff] = useState(() => { try { return new Set(JSON.parse(localStorage.getItem(`equipHintOff:${bid.bid_id}`) || "[]")); } catch { return new Set(); } });
  const dismissEquipHint = (grp) => setEquipHintOff((s) => { const n = new Set(s); n.add(grp); try { localStorage.setItem(`equipHintOff:${bid.bid_id}`, JSON.stringify([...n])); } catch { /* ignore */ } return n; });
  const [equipCatalog, setEquipCatalog] = useState([]);
  const [equipCategories, setEquipCategories] = useState([]);
  const [commodityIdx, setCommodityIdx] = useState([]);
  const [collapsed, setCollapsed] = useState(new Set());
  const [collapsedSubs, setCollapsedSubs] = useState(new Set());
  const toggleSubCollapse = (g, s) => setCollapsedSubs((c) => { const k = `${g}::${s}`; const n = new Set(c); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const [expanded, setExpanded] = useState(new Set());
  // Step rows: open by default; what a person closes is remembered per bid per person.
  const { user: me } = useAuth();
  const stepKey = `gf-steps-closed:${bid.bid_id}:${me?.user_id || "me"}`;
  const [stepsClosed, setStepsClosed] = useState(() => { try { return new Set(JSON.parse(localStorage.getItem(stepKey) || "[]")); } catch { return new Set(); } });
  const saveClosed = (n) => { setStepsClosed(n); try { localStorage.setItem(stepKey, JSON.stringify([...n])); } catch { /* ignore */ } };
  const toggleSteps = (id) => { const n = new Set(stepsClosed); n.has(id) ? n.delete(id) : n.add(id); saveClosed(n); };
  const setGroupSteps = (ids, open) => { const n = new Set(stepsClosed); ids.forEach((id) => (open ? n.delete(id) : n.add(id))); saveClosed(n); };
  const [steps, setSteps] = useState({});
  useEffect(() => { api.get(`/bids/${bid.bid_id}/line-steps`).then((r) => setSteps(r.data || {})).catch(() => {}); }, [bid.bid_id, computed]);
  const [density, setDensity] = useState(() => { try { return localStorage.getItem("gridforge-grid-density") || "comfortable"; } catch { return "comfortable"; } });
  const toggleDensity = () => setDensity((d) => { const next = d === "compact" ? "comfortable" : "compact"; try { localStorage.setItem("gridforge-grid-density", next); } catch { /* ignore */ } return next; });
  const rowPad = density === "compact" ? "py-1" : "py-2.5";
  const toggleExpand = (id) => setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const drag = useRef(null);

  useEffect(() => { api.get("/cost-codes").then((r) => setCodes([...(r.data.standard || []), ...(r.data.custom || [])])).catch(() => {}); }, []);
  useEffect(() => { api.get("/assemblies").then((r) => setAssemblies(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/group-crews").then((r) => setCrewLib(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/equipment-catalog").then((r) => setEquipCatalog(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/catalog/settings").then((r) => setEquipCategories(r.data.equipment_categories || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/commodity-indices").then((r) => setCommodityIdx(r.data || [])).catch(() => {}); }, []);
  const idxNameMap = useMemo(() => Object.fromEntries((commodityIdx || []).map((i) => [i.index_id, i.name])), [commodityIdx]);
  const lineIdxLabel = (l) => {
    if (l.commodity_index_id) return idxNameMap[l.commodity_index_id] || "Indexed";
    const comps = l.commodity_components;
    if (comps && comps.length) { const names = [...new Set(comps.map((c) => idxNameMap[c.index_id]).filter(Boolean))]; return names.join(", ") || "Indexed"; }
    return null;
  };
  const saveCrewToLib = async (name, crew) => {
    try { const r = await api.post("/group-crews", { name, crew, source: { kind: "estimate", project_id: bid.bid_id, project_name: bid.name } }); setCrewLib((l) => [r.data, ...l.filter((x) => x.id !== r.data.id)]); toast.success("Crew saved to library"); }
    catch { toast.error("Could not save crew to library"); }
  };
  useEffect(() => {
    api.get("/rate-libraries").then((r) => {
      const active = (r.data || []).find((x) => x.is_active) || (r.data || [])[0];
      const roles = active?.labor_roles || [];
      if (roles.length) setBlendedRate(+(roles.reduce((a, x) => a + (Number(x.hourly_rate) || 0), 0) / roles.length).toFixed(2));
    }).catch(() => {});
  }, []);

  const codeName = (code) => codes.find((c) => c.code === code)?.name;

  const lineAmt = useMemo(() => {
    const map = {};
    (computed?.lines || computed?.line_results || []).forEach((r) => { if (r.line_id) map[r.line_id] = r.subtotal ?? r.total; });
    return map;
  }, [computed]);
  const lineResult = useMemo(() => {
    const map = {};
    (computed?.lines || computed?.line_results || []).forEach((r) => { if (r.line_id) map[r.line_id] = r; });
    return map;
  }, [computed]);

  const persist = (next) => { setLines(next); updateBid({ scope_lines: next.map((l) => ({ ...l, quantity: eff(l), budget_code: l.cost_code || "" })) }); };
  const persistGroups = (next) => { setScopeGroups(next); updateBid({ scope_groups: next }); };
  const persistSubgroups = (next) => { setScopeSubgroups(next); updateBid({ scope_subgroups: next }); };
  const subsOfGroup = (group) => scopeSubgroups.filter((s) => s.group === group);
  const addSubgroup = (group) => {
    const name = window.prompt(`New subgroup inside "${group}":`, "New Subgroup");
    const nm = (name || "").trim();
    if (!nm) return;
    if (scopeSubgroups.some((s) => s.group === group && s.name === nm)) { toast.error("That subgroup already exists in this scope."); return; }
    persistSubgroups([...scopeSubgroups, { group, name: nm, color: DEFAULT_SUBGROUP_COLOR, show_on_proposal: true }]);
  };
  const setSubColor = (group, name, color) => persistSubgroups(scopeSubgroups.map((s) => (s.group === group && s.name === name ? { ...s, color } : s)));
  const toggleSubProposal = (group, name) => persistSubgroups(scopeSubgroups.map((s) => (s.group === group && s.name === name ? { ...s, show_on_proposal: s.show_on_proposal === false } : s)));
  const renameSubgroup = (group, oldName, newName) => {
    const nm = (newName || "").trim();
    if (!nm || nm === oldName) return;
    if (scopeSubgroups.some((s) => s.group === group && s.name === nm)) { toast.error("That subgroup name is taken in this scope."); return; }
    const nextLines = lines.map((l) => (l.group === group && l.subgroup === oldName ? { ...l, subgroup: nm } : l));
    const nextSubs = scopeSubgroups.map((s) => (s.group === group && s.name === oldName ? { ...s, name: nm } : s));
    setLines(nextLines); setScopeSubgroups(nextSubs);
    updateBid({ scope_lines: nextLines.map((l) => ({ ...l, quantity: eff(l), budget_code: l.cost_code || "" })), scope_subgroups: nextSubs });
  };
  const deleteSubgroup = (group, name) => {
    const nextLines = lines.map((l) => (l.group === group && l.subgroup === name ? { ...l, subgroup: null } : l));
    const nextSubs = scopeSubgroups.filter((s) => !(s.group === group && s.name === name));
    setLines(nextLines); setScopeSubgroups(nextSubs);
    updateBid({ scope_lines: nextLines.map((l) => ({ ...l, quantity: eff(l), budget_code: l.cost_code || "" })), scope_subgroups: nextSubs });
  };
  const crewOf = (name) => scopeGroups.find((g) => g.name === name)?.crew || null;
  const groupCodeOf = (name) => scopeGroups.find((g) => g.name === name)?.cost_code || "";
  const setGroupCode = (name, code) => {
    const exists = scopeGroups.some((g) => g.name === name);
    const next = exists ? scopeGroups.map((g) => (g.name === name ? { ...g, cost_code: code } : g)) : [...scopeGroups, { name, cost_code: code }];
    persistGroups(next);
  };
  const saveCrew = (name, crew) => {
    const exists = scopeGroups.some((g) => g.name === name);
    const next = exists ? scopeGroups.map((g) => (g.name === name ? { ...g, crew } : g)) : [...scopeGroups, { name, crew }];
    persistGroups(next);
  };
  const econOf = (name) => (computed?.groups || {})[name] || null;
  const equipOf = (name) => scopeGroups.find((g) => g.name === name)?.equipment || [];
  const saveEquip = (name, equipment) => {
    const exists = scopeGroups.some((g) => g.name === name);
    const next = exists ? scopeGroups.map((g) => (g.name === name ? { ...g, equipment } : g)) : [...scopeGroups, { name, equipment }];
    persistGroups(next);
  };
  const saveCrewEquip = (name, crew, equipment) => {
    const exists = scopeGroups.some((g) => g.name === name);
    const next = exists ? scopeGroups.map((g) => (g.name === name ? { ...g, crew, equipment } : g)) : [...scopeGroups, { name, crew, equipment }];
    persistGroups(next);
  };
  const saveEquipToCatalog = async (spec, silent) => {
    try {
      const { qty, ...rest } = spec;
      const r = await api.post("/equipment-catalog", rest);
      setEquipCatalog((l) => [r.data, ...l.filter((x) => x.equipment_id !== r.data.equipment_id)]);
      if (r.data?.equipment_id) api.post(`/catalog/${r.data.equipment_id}/use`, { project_id: bid.bid_id, project_name: bid.name }).catch(() => {});
      if (!silent) toast.success(`“${r.data.name}” saved to the equipment catalog`);
    } catch { if (!silent) toast.error("Could not save to catalog"); }
  };

  // ── takeoff measurement → new priced line ──
  useEffect(() => {
    if (!pendingMeasure) return;
    const m = pendingMeasure;
    const l = {
      line_id: nid(), group: "From Takeoff", cost_code: "", task_type_id: "",
      name: m.label || "From takeoff", unit: m.unit || "ea",
      base_quantity: Number(m.value) || 0, multiplier: 1, waste_pct: 0, quantity: Number(m.value) || 0,
      source: "takeoff", markup_id: m.markup_id,
    };
    persist([...lines, l]);
    setCollapsed((c) => { const n = new Set(c); n.delete("From Takeoff"); return n; });
    clearPending && clearPending();
    // eslint-disable-next-line
  }, [pendingMeasure]);

  const patch = (id, changes) => { fadeHighlight(id); persist(lines.map((l) => (l.line_id === id ? { ...l, ...changes } : l))); };
  const setField = (id, field, val) => patch(id, { [field]: val });
  // Grey kind hints for untagged lines (same rules as the catalog auto-tag); one click confirms.
  const [kindHints, setKindHints] = useState({});
  const [tkFrom, setTkFrom] = useState({});
  useEffect(() => {
    api.get(`/bids/${bid.bid_id}/takeoff-from`).then((r) => setTkFrom(r.data || {})).catch(() => {});
  }, [bid.bid_id, (bid.scope_lines || []).length]); // eslint-disable-line react-hooks/exhaustive-deps
  const untaggedKey = lines.filter((l) => !l.kind && (l.name || "").trim()).map((l) => `${l.line_id}:${l.name}`).join("|");
  useEffect(() => {
    const items = lines.filter((l) => !l.kind && (l.name || "").trim()).map((l) => ({ id: l.line_id, name: l.name, cost_code: l.cost_code }));
    if (!items.length) { setKindHints({}); return undefined; }
    const t = setTimeout(() => { api.post("/kinds/suggest", { items }).then((r) => setKindHints(r.data.suggestions || {})).catch(() => {}); }, 500);
    return () => clearTimeout(t);
  }, [untaggedKey]); // eslint-disable-line
  // Two-way sync: editing an assembly line's component tree re-derives its per-unit labor/material
  // and stores the edited tree on takeoff.assembly_tree (same shape the takeoff page reads).
  const onTree = (id, tree) => {
    const pu = treePerUnit(tree);
    const l = lines.find((x) => x.line_id === id) || {};
    patch(id, {
      unit_labor_hours: +pu.mh_per_unit.toFixed(4),
      unit_material_cost: +pu.material_per_unit.toFixed(4),
      takeoff: { ...(l.takeoff || {}), assembly_tree: tree },
    });
  };

  const pickAssembly = (id, a) => {
    if (!a) { patch(id, { assembly_id: null, commodity_components: [], commodity_fixed_material: null, commodity_base_material: null }); return; }
    const line = lines.find((l) => l.line_id === id) || {};
    const idxMap = Object.fromEntries((commodityIdx || []).map((i) => [i.index_id, i]));
    const f = assemblyCommodity(a, idxMap);
    const changes = {
      assembly_id: a.assembly_id,
      name: (line.name && line.name !== "New line") ? line.name : a.name,
      cost_code: a.cost_code || line.cost_code || "",
      unit: line.unit || UOM_OF[a.measure_type] || "ea",
      unit_material_cost: f.unit_material_cost || "",
      unit_labor_hours: f.unit_labor_hours || "",
      labor_basis: a.time_basis === "crew" ? "crew" : "man",
      commodity_components: f.commodity_components,
      commodity_fixed_material: f.commodity_fixed_material,
      commodity_base_material: f.commodity_base_material,
      commodity_index_id: null,
      commodity_baseline_price: null,
    };
    fadeHighlight(id);
    const nextLines = lines.map((l) => (l.line_id === id ? { ...l, ...changes } : l));
    // Scope cost-code autofill: the first assembly dropped into a scope stamps the scope's code.
    const grp = line.group;
    let nextGroups = scopeGroups;
    if (a.cost_code && grp && !groupCodeOf(grp)) {
      const exists = scopeGroups.some((g) => g.name === grp);
      nextGroups = exists ? scopeGroups.map((g) => (g.name === grp ? { ...g, cost_code: a.cost_code } : g)) : [...scopeGroups, { name: grp, cost_code: a.cost_code }];
    }
    setLines(nextLines);
    if (nextGroups !== scopeGroups) setScopeGroups(nextGroups);
    updateBid({ scope_lines: nextLines.map((l) => ({ ...l, quantity: eff(l), budget_code: l.cost_code || "" })), scope_groups: nextGroups });
    if (a?.assembly_id) api.post(`/catalog/${a.assembly_id}/use`, { project_id: bid.bid_id, project_name: bid.name }).catch(() => {});
    setExpanded((s) => new Set(s).add(id));
  };

  const addLine = (group, subgroup = null) => {
    const id = nid();
    persist([...lines, { line_id: id, group, subgroup, cost_code: "", task_type_id: "", name: "New line", unit: "ea", base_quantity: 0, multiplier: 1, waste_pct: 0, quantity: 0 }]);
    setExpanded((s) => new Set(s).add(id));
  };
  const removeLine = (id) => persist(lines.filter((l) => l.line_id !== id));
  // Duplicate a line into an independent editable copy (carries its edited component tree + costs,
  // drops the takeoff geometry / markup link so it's a standalone estimate line).
  const duplicateLine = (l) => {
    const { takeoff, source, markup_id, ...rest } = l;
    const copy = { ...rest, line_id: nid(), name: `${l.name || "Line"} (copy)` };
    if (takeoff?.assembly_tree) copy.takeoff = { assembly_tree: takeoff.assembly_tree };
    const idx = lines.findIndex((x) => x.line_id === l.line_id);
    const next = [...lines]; next.splice(idx + 1, 0, copy); persist(next);
    setExpanded((s) => new Set(s).add(copy.line_id));
  };
  const addGroup = () => { const name = window.prompt("New scope (group) name:", "New Scope"); if (name) { const id = nid(); persist([...lines, { line_id: id, group: name, cost_code: "", task_type_id: "", name: "New line", unit: "ea", base_quantity: 0, multiplier: 1, waste_pct: 0, quantity: 0 }]); setExpanded((s) => new Set(s).add(id)); } };

  const renameGroup = (oldName, newName) => {
    const nm = (newName || "").trim();
    if (!nm || nm === oldName) return;
    const nextLines = lines.map((l) => (l.group === oldName ? { ...l, group: nm } : l));
    const nextGroups = scopeGroups.map((g) => (g.name === oldName ? { ...g, name: nm } : g));
    const nextSubs = scopeSubgroups.map((s) => (s.group === oldName ? { ...s, group: nm } : s));
    setLines(nextLines); setScopeGroups(nextGroups); setScopeSubgroups(nextSubs);
    updateBid({ scope_lines: nextLines.map((l) => ({ ...l, quantity: eff(l), budget_code: l.cost_code || "" })), scope_groups: nextGroups, scope_subgroups: nextSubs });
    setCollapsed((c) => { if (!c.has(oldName)) return c; const n = new Set(c); n.delete(oldName); n.add(nm); return n; });
  };
  const toggleCollapse = (g) => setCollapsed((c) => { const n = new Set(c); n.has(g) ? n.delete(g) : n.add(g); return n; });

  const groupOrder = useMemo(() => {
    const seen = [];
    lines.forEach((l) => { if (!seen.includes(l.group)) seen.push(l.group); });
    return seen;
  }, [lines]);
  const groups = useMemo(() => {
    const g = {};
    lines.forEach((l) => { (g[l.group] = g[l.group] || []).push(l); });
    return g;
  }, [lines]);

  // ── drag & drop reorder ──
  const dropOnLine = (targetLine) => {
    const d = drag.current; drag.current = null;
    if (!d || d.kind !== "line" || d.id === targetLine.line_id) return;
    const dragged = lines.find((l) => l.line_id === d.id);
    if (!dragged) return;
    const rest = lines.filter((l) => l.line_id !== d.id);
    const moved = { ...dragged, group: targetLine.group, subgroup: targetLine.subgroup ?? null };
    const ti = rest.findIndex((l) => l.line_id === targetLine.line_id);
    rest.splice(ti, 0, moved);
    persist(rest);
  };
  const dropOnGroup = (targetGroup) => {
    const d = drag.current; drag.current = null;
    if (!d) return;
    if (d.kind === "line") {
      const dragged = lines.find((l) => l.line_id === d.id);
      if (!dragged) return;
      const rest = lines.filter((l) => l.line_id !== d.id);
      let insertAt = rest.length;
      for (let i = rest.length - 1; i >= 0; i--) { if (rest[i].group === targetGroup) { insertAt = i + 1; break; } }
      rest.splice(insertAt, 0, { ...dragged, group: targetGroup, subgroup: null });
      persist(rest);
    } else if (d.kind === "group") {
      if (d.name === targetGroup) return;
      const order = groupOrder.filter((g) => g !== d.name);
      const ti = order.indexOf(targetGroup);
      order.splice(ti, 0, d.name);
      const next = [];
      order.forEach((g) => (groups[g] || []).forEach((l) => next.push(l)));
      persist(next);
    }
  };

  const asmById = (id) => assemblies.find((a) => a.assembly_id === id);

  return (
    <div className="p-4" data-testid="estimating-grid">

      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Layers className="w-4 h-4 text-[#003E87]" /> Estimate</div>
          <div className="text-[11px] text-slate-400 mt-0.5">{lines.length} line{lines.length === 1 ? "" : "s"} across {groupOrder.length} scope{groupOrder.length === 1 ? "" : "s"} · build lines from your reusable assemblies</div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={toggleDensity} data-testid="grid-density-toggle" title={density === "compact" ? "Switch to comfortable rows" : "Switch to compact rows"}><Rows3 className="w-4 h-4 mr-1.5" /> {density === "compact" ? "Compact" : "Comfortable"}</Button>
          <Button size="sm" variant="outline" onClick={() => window.open(`/bids/${bid.bid_id}/boe/print`, "_blank")} data-testid="export-boe"><FileText className="w-4 h-4 mr-1.5" /> Basis of Estimate</Button>
          {!readOnly && <Button size="sm" onClick={addGroup} data-testid="add-group" className="bg-[#003E87] hover:bg-[#00306a] text-white"><FolderPlus className="w-4 h-4 mr-1.5" /> Add scope</Button>}
        </div>
      </div>

      {diff && (addedSet.size + changedSet.size) > 0 && (
        <div className="flex items-center justify-between mb-3 px-3 py-2 rounded-lg bg-[#F4C400]/10 border border-[#F4C400]/40" data-testid="import-diff-banner">
          <span className="text-xs text-[#7a6400] flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Imported changes — <b className="text-emerald-700">{addedSet.size} added</b> · <b className="text-[#8a6d00]">{changedSet.size} changed</b> (highlighted below)</span>
          <button onClick={clearHighlights} className="text-[11px] text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="clear-import-diff"><X className="w-3 h-3" /> Clear</button>
        </div>
      )}

      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
        <div className={`grid gap-3 px-4 ${rowPad} bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400 font-semibold border-b border-slate-100`} style={{ gridTemplateColumns: COLS }}>
          <div /><div>Line item</div><div>Cost code</div><div>UoM</div><div className="text-right">Qty</div><div className="text-right">Total</div><div className="text-right">Man-hrs</div><div className="text-right">Amount</div><div />
        </div>

        {groupOrder.map((group) => {
          const gl = groups[group] || [];
          const gEcon = econOf(group);
          const gLines = gl.reduce((a, l) => a + (lineAmt[l.line_id] || 0), 0);
          const gEquip = Number(gEcon?.equipment_cost) || 0;
          const gPerDiem = Number(gEcon?.per_diem_cost) || 0;
          const gTotal = gLines + gEquip + gPerDiem;
          const gDetail = gEcon?.scope_cost_detail || (gEquip || gPerDiem ? `lines ${money(gLines)}${gEquip ? ` + crew equipment ${money(gEquip)}` : ""}${gPerDiem ? ` + crew per-diem ${money(gPerDiem)}` : ""} = ${money(gTotal)}` : "");
          const isCollapsed = collapsed.has(group);
          return (
            <div key={group} className="border-b border-slate-100 last:border-b-0">
              <div className="flex items-center justify-between pl-2 pr-4 py-2 bg-[#003E87]/[0.035] border-l-2 border-[#003E87]"
                data-testid={`group-${group}`}
                draggable={!readOnly}
                onDragStart={() => { if (!readOnly) drag.current = { kind: "group", name: group }; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => dropOnGroup(group)}>
                <div className="flex items-center gap-1.5 min-w-0">
                  {!readOnly && <GripVertical className="w-3.5 h-3.5 text-slate-300 cursor-grab shrink-0" />}
                  <button onClick={() => toggleCollapse(group)} data-testid={`collapse-${group}`} className="text-[#003E87] shrink-0">
                    {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {readOnly ? (
                    <span className="text-sm font-semibold text-[#003E87] truncate">{group}</span>
                  ) : (
                    <input defaultValue={group} key={group}
                      onBlur={(e) => renameGroup(group, e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                      data-testid={`group-name-${group}`}
                      className="text-sm font-semibold text-[#003E87] bg-transparent border border-transparent hover:border-slate-200 focus:border-[#003E87] focus:bg-white rounded px-1.5 py-0.5 min-w-[80px] max-w-[280px]" />
                  )}
                  <span className="text-[11px] text-slate-400 shrink-0 ml-1">{gl.length} line{gl.length === 1 ? "" : "s"}</span>
                  {(() => { const ids = gl.filter((l) => steps[l.line_id]).map((l) => l.line_id); if (!ids.length) return null; const allOpen = ids.every((id) => !stepsClosed.has(id)); return (
                    <button onClick={() => setGroupSteps(ids, !allOpen)} data-testid={`steps-toggle-all-${group}`} className="text-[11px] text-[#003E87] hover:underline shrink-0 ml-2">{allOpen ? "Collapse all" : "Expand all"} steps</button>); })()}
                </div>
                <div className="flex items-center gap-3">
                  {!readOnly ? (
                    <CostCodeCombobox value={groupCodeOf(group)} onChange={(v) => setGroupCode(group, v)}
                      testid={`group-cc-${group}`} inherit="scope cost code"
                      className="hidden sm:block w-44 text-[11px] font-mono" />
                  ) : (groupCodeOf(group) ? <span className="text-[11px] font-mono text-[#003E87] bg-[#003E87]/[0.07] rounded px-1.5 py-0.5" data-testid={`group-cc-${group}`}>{groupCodeOf(group)}</span> : null)}
                  {(() => { const e = econOf(group); return (
                    <span className="hidden md:flex items-center gap-3 text-[11px] text-slate-600 font-mono" data-testid={`group-crew-summary-${group}`}>
                      <span title="Man-hours for this scope" data-testid={`group-mh-${group}`}>{e?.man_hours ? `${num(e.man_hours)} MH` : "— MH"}</span>
                      <span title="Work days from the crew's production rate" data-testid={`group-days-${group}`}>{e?.days != null ? `${num(e.days)} days` : "— days"}</span>
                    </span>
                  ); })()}
                  {!readOnly && <button onClick={() => toggleCrewPopup(group)} data-testid={`crew-btn-${group}`} title={econOf(group) ? `${econOf(group).headcount} people · $${num(econOf(group).effective_per_manhour)}/hr${econOf(group).per_diem_cost ? ` · ${money(econOf(group).per_diem_cost)} per diem` : ""}${equipOf(group).length ? ` · ${equipOf(group).length} machines` : ""}` : "Build the crew and equipment"}
                    className={`text-xs font-medium flex items-center gap-1 rounded px-2 py-1 ${crewPopup === group ? "bg-[#003E87] text-white" : crewOf(group) ? "text-[#003E87] hover:bg-[#003E87]/10" : "text-slate-400 hover:bg-slate-100"}`}><Users className="w-3.5 h-3.5" /> {crewOf(group) ? "Crew & equipment" : "Build crew"}</button>}
                  <span className="text-sm font-mono font-semibold text-slate-700" data-testid={`group-subtotal-${group}`} title={gDetail || undefined}>{gTotal ? money(gTotal) : ""}</span>
                  {!readOnly && <button onClick={() => addLine(group)} data-testid={`add-line-${group}`} className="text-[#003E87] hover:bg-[#003E87]/10 rounded px-2 py-1 text-xs font-medium flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> line</button>}
                  {!readOnly && <button onClick={() => addSubgroup(group)} data-testid={`add-subgroup-${group}`} className="text-[#003E87] hover:bg-[#003E87]/10 rounded px-2 py-1 text-xs font-medium flex items-center gap-1"><FolderTree className="w-3.5 h-3.5" /> subgroup</button>}
                </div>
              </div>
              {(() => {
                const needsCrew = gl.some((l) => Number(l.unit_labor_hours) > 0 || l.labor_basis === "crew");
                const hasCrew = (crewOf(group)?.members || []).length > 0;
                return needsCrew && !hasCrew ? (
                  <div className="flex items-center gap-2 px-4 py-1.5 bg-amber-50 border-t border-amber-100 text-[11px] text-amber-700" data-testid={`labor-unpriced-${group}`}>
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    <span>Labor unpriced — this scope has time-based lines but no crew.
                      {!readOnly && <button onClick={() => toggleCrewPopup(group)} className="underline hover:text-amber-900 ml-1" data-testid={`labor-unpriced-fix-${group}`}>Build a crew</button>}
                    </span>
                  </div>
                ) : null;
              })()}
              {(() => {
                const hasLabor = gl.some((l) => (l.enabled !== false) && (Number(l.unit_labor_hours) > 0 || l.labor_basis === "crew" || Number(l.unit_labor_cost) > 0));
                const hasEquip = equipOf(group).length > 0;
                return hasLabor && !hasEquip && !equipHintOff.has(group) ? (
                  <div className="flex items-center gap-2 px-4 py-1.5 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500" data-testid={`equip-missing-${group}`}>
                    <Info className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                    <span>No equipment on this scope — add it if this work needs machines, or skip if it doesn't.
                      {!readOnly && <button onClick={() => toggleCrewPopup(group)} className="underline hover:text-slate-700 ml-1" data-testid={`equip-missing-fix-${group}`}>Add equipment</button>}
                    </span>
                    <button onClick={() => dismissEquipHint(group)} className="ml-auto text-slate-300 hover:text-slate-600" title="Dismiss" data-testid={`equip-missing-dismiss-${group}`}><X className="w-3 h-3" /></button>
                  </div>
                ) : null;
              })()}
              {crewPopup === group && (
                <CrewEquipPanel key={`crewequip-${group}`} group={group} crew={crewOf(group)} equipment={equipOf(group)} econ={econOf(group)} readOnly={readOnly}
                  weather={bid.location_profile?.weather} bidBurden={bid.labor_burden} onBidBurden={(lb) => updateBid({ labor_burden: lb })}
                  projHours={bid.work_basis?.hours_per_day ?? 10} apprenticeshipPct={bid.apprenticeship_pct ?? 0}
                  library={crewLib} onSaveToLibrary={saveCrewToLib}
                  equipCatalog={equipCatalog} equipCategories={equipCategories}
                  location={{ diesel: bid.location_profile?.diesel?.price, tax: computed?.totals?.global_tax_pct ?? bid.location_profile?.tax?.combined,
                    tax_pct: computed?.totals?.global_tax_pct ?? bid.sales_tax?.rate_pct ?? bid.location_profile?.tax?.combined, env_pct: computed?.totals?.global_env_pct,
                    window_days: computed?.gc_drivers?.work_days }}
                  workDays={econOf(group)?.work_days}
                  onSave={({ crew, equipment }) => saveCrewEquip(group, crew, equipment)}
                  onSaveToCatalog={saveEquipToCatalog}
                  onClose={() => setCrewPopup(null)} />
              )}

              {!isCollapsed && sectionize(gl, scopeSubgroups, group).map((sec) => (
                <React.Fragment key={sec.sub || "__ungrouped__"}>
                  {sec.sub && (
                    <SubgroupBand group={group} sub={sec} readOnly={readOnly}
                      collapsed={collapsedSubs.has(`${group}::${sec.sub}`)} onToggle={() => toggleSubCollapse(group, sec.sub)}
                      subtotal={sec.lines.reduce((a, l) => a + (lineAmt[l.line_id] || 0), 0)} lineCount={sec.lines.length}
                      onColor={setSubColor} onToggleProposal={toggleSubProposal} onRename={renameSubgroup}
                      onDelete={deleteSubgroup} onAddLine={() => addLine(group, sec.sub)} />
                  )}
                  {(!sec.sub || !collapsedSubs.has(`${group}::${sec.sub}`)) && sec.lines.map((l) => {
                const asm = l.assembly_id ? asmById(l.assembly_id) : null;
                const isOpen = expanded.has(l.line_id);
                const amt = lineAmt[l.line_id];
                const mh = lineResult[l.line_id]?.man_hours;
                return (
                  <React.Fragment key={l.line_id}>
                    <div data-testid={`grid-line-${l.line_id}`} data-line-id={l.line_id}
                      draggable={!readOnly}
                      onDragStart={() => { if (!readOnly) drag.current = { kind: "line", id: l.line_id }; }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => dropOnLine(l)}
                      className={`grid gap-3 px-4 ${rowPad} border-t border-slate-50 items-center transition-colors ${isOpen ? "bg-[#003E87]/[0.03]" : "hover:bg-slate-50/70"} ${addedSet.has(l.line_id) ? "bg-emerald-50" : changedSet.has(l.line_id) ? "bg-[#F4C400]/10" : ""}`}
                      style={{ gridTemplateColumns: COLS, ...(sec.color ? { boxShadow: `inset 3px 0 0 ${sec.color}` } : {}) }}>
                      {!readOnly ? <GripVertical className="w-3.5 h-3.5 text-slate-300 cursor-grab" data-testid={`drag-${l.line_id}`} /> : <span />}

                      <div className="min-w-0">
                        <input disabled={readOnly} value={l.name || ""} placeholder="Name this line…"
                          onChange={(e) => setField(l.line_id, "name", e.target.value)}
                          data-testid={`name-${l.line_id}`}
                          className="w-full text-sm text-slate-800 bg-transparent border border-transparent hover:border-slate-200 focus:border-[#003E87] focus:bg-white rounded px-1.5 py-1 disabled:opacity-70" />
                        <div className="flex items-center gap-1.5 mt-0.5 pl-1.5 flex-nowrap overflow-hidden whitespace-nowrap" data-testid={`line-chips-${l.line_id}`}>{steps[l.line_id] && <button type="button" onClick={() => toggleSteps(l.line_id)} data-testid={`steps-chevron-${l.line_id}`} title={stepsClosed.has(l.line_id) ? "Show the steps" : "Hide the steps"}
                            className="inline-flex items-center gap-0.5 text-[11px] px-1 py-0.5 rounded text-[#003E87] hover:bg-[#003E87]/10">{stepsClosed.has(l.line_id) ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}{steps[l.line_id].sum.steps} steps</button>}
                          {asm && <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-[#003E87]/10 text-[#003E87] font-medium shrink-0 max-w-[110px] truncate" title={asm.name} data-testid={`asm-chip-${l.line_id}`}><Boxes className="w-2.5 h-2.5 shrink-0" /> <span className="truncate">{asm.cost_code || (asm.name || "").split(/[\s—-]/)[0]}</span></span>}
                          {lineIdxLabel(l) && <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-medium" data-testid={`line-index-chip-${l.line_id}`} title="Material is linked to a commodity index — reprice on a revision to update"><TrendingUp className="w-2.5 h-2.5" /> {lineIdxLabel(l)}</span>}
                          {l.task_type_id && !asm && <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium"><Wrench className="w-2.5 h-2.5" /> calculator</span>}
                          {l.source === "takeoff" && !tkFrom[l.line_id] && <span className="text-[11px] px-1.5 py-0.5 rounded bg-[#F4C400]/30 text-[#7a6400] font-medium" title="Measured on the takeoff">takeoff</span>}
                          {tkFrom[l.line_id] && (() => { const t = tkFrom[l.line_id]; const d0 = t.drawings[0] || {}; return (
                            <a href={`/bids/${bid.bid_id}?tab=takeoff&drawing=${d0.drawing_id}${d0.first_markup_id ? `&markup=${d0.first_markup_id}` : ""}`} data-testid={`takeoff-from-${l.line_id}`}
                              title={`Layer ${t.layer}: ${t.drawings.map((d) => `${d.name} (${d.marks})`).join(", ")}. Opens the drawing.`}
                              className="text-[11px] px-1.5 py-0.5 rounded bg-[#F4C400]/30 text-[#7a6400] font-medium hover:underline">from takeoff: {t.marks} mark{t.marks === 1 ? "" : "s"} on {t.sheets} sheet{t.sheets === 1 ? "" : "s"}</a>); })()}
                          {l.is_subcontract && <span className="text-[11px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-medium">subcontract</span>}
                          <ProvenanceChip basis={l.basis} grade={l.grade} testid={`prov-chip-${l.line_id}`} />
                          {l.kind && <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium" title="Proposal quantity tag" data-testid={`kind-chip-${l.line_id}`}>{KIND_LABELS[l.kind] || l.kind}</span>}
                          {!l.kind && kindHints[l.line_id] && !readOnly && (
                            <button type="button" onClick={(e) => { e.stopPropagation(); patch(l.line_id, { kind: kindHints[l.line_id].kind }); }} data-testid={`kind-hint-${l.line_id}`}
                              className="text-[11px] px-1.5 py-0.5 rounded border border-dashed border-slate-300 text-slate-400 hover:text-[#003E87] hover:border-[#003E87] transition-colors"
                              title="Suggested proposal quantity tag from the name · click to use it">{kindHints[l.line_id].label}?</button>
                          )}
                        </div>
                      </div>

                      <div className="min-w-0">
                        {l.cost_code
                          ? <span className="inline-block text-[11px] font-mono text-[#003E87] bg-[#003E87]/[0.07] rounded px-1.5 py-1 truncate max-w-full" title={codeName(l.cost_code) || l.cost_code} data-testid={`code-chip-${l.line_id}`}>{l.cost_code}</span>
                          : groupCodeOf(l.group)
                            ? <span className="inline-block text-[11px] font-mono text-slate-400 bg-slate-100 rounded px-1.5 py-1 truncate max-w-full" title={`Inherited from scope${codeName(groupCodeOf(l.group)) ? " · " + codeName(groupCodeOf(l.group)) : ""}`} data-testid={`code-chip-${l.line_id}`}>{groupCodeOf(l.group)}</span>
                            : <span className="text-[11px] text-slate-300">—</span>}
                      </div>

                      <div className="text-xs text-slate-500">{l.unit || "—"}</div>

                      <input type="number" disabled={readOnly} value={l.base_quantity === 0 || l.base_quantity == null ? "" : l.base_quantity} onChange={(e) => setField(l.line_id, "base_quantity", e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-right focus:border-[#003E87] outline-none" data-testid={`qty-${l.line_id}`} placeholder="0" />

                      <div className="text-xs text-right font-mono text-slate-500">{num(eff(l))}</div>

                      <div className="text-xs text-right font-mono text-slate-500" data-testid={`mh-${l.line_id}`} title="Man-hours for this line">{mh ? num(mh) : <span className="text-slate-300">—</span>}</div>

                      <div className="text-right min-w-0">
                        <div className="text-sm font-mono font-semibold text-slate-900 truncate" data-testid={`amount-${l.line_id}`}
                          title={Number(lineResult[l.line_id]?.equipment_share) > 0 ? `+${money(lineResult[l.line_id].equipment_share)} crew equipment share${lineResult[l.line_id].all_in_unit_cost != null ? ` · ${money(lineResult[l.line_id].all_in_unit_cost, 2)}/${l.unit || "ea"} all-in` : ""}` : undefined}>{amt != null ? money(amt) : <span className="text-slate-300 font-normal">—</span>}</div>
                      </div>

                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => toggleExpand(l.line_id)} title="Edit this line" data-testid={`edit-${l.line_id}`} className={`rounded p-1 ${isOpen ? "bg-[#003E87] text-white" : "text-slate-400 hover:bg-slate-100 hover:text-[#003E87]"}`}>{isOpen ? <ChevronDown className="w-4 h-4" /> : <Pencil className="w-3.5 h-3.5" />}</button>
                        {!readOnly && <button onClick={() => duplicateLine(l)} title="Duplicate line" data-testid={`dup-${l.line_id}`} className="text-slate-300 hover:text-[#003E87] p-1"><Copy className="w-3.5 h-3.5" /></button>}
                        {!readOnly && <button onClick={() => removeLine(l.line_id)} className="text-slate-300 hover:text-rose-600 p-1" data-testid={`del-${l.line_id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                      </div>
                    </div>

                    {steps[l.line_id] && !stepsClosed.has(l.line_id) && <LineStepRows lineId={l.line_id} steps={steps[l.line_id]} unit={l.unit} />}
                    {isOpen && (
                      <LineEditor l={l} asm={asm} assemblies={assemblies} codes={codes} codeName={codeName} kindHint={kindHints[l.line_id]}
                        result={lineResult[l.line_id]} readOnly={readOnly} groupRate={econOf(l.group)?.effective_per_manhour} groupCode={groupCodeOf(l.group)}
                        subgroups={subsOfGroup(l.group).map((s) => s.name)}
                        onField={(f, v) => setField(l.line_id, f, v)} onPickAssembly={(a) => pickAssembly(l.line_id, a)} onTree={(t) => onTree(l.line_id, t)}
                        indices={commodityIdx} project={{ project_id: bid.bid_id, project_name: bid.name }} onReloadAssemblies={() => api.get("/assemblies").then((r) => setAssemblies(r.data || [])).catch(() => {})} />
                    )}
                  </React.Fragment>
                );
              })}
                </React.Fragment>
              ))}
              {!isCollapsed && (gEquip > 0 || gPerDiem > 0) && (
                <ScopeCarriedRows group={group} econ={gEcon} rowPad={rowPad} readOnly={readOnly} onOpen={() => toggleCrewPopup(group)} />
              )}
            </div>
          );
        })}

        {lines.length === 0 && (
          <div className="px-3 py-12 text-center">
            <Boxes className="w-8 h-8 text-slate-200 mx-auto mb-2" />
            <div className="text-sm text-slate-500">No lines yet.</div>
            <div className="text-xs text-slate-400 mt-1">{readOnly ? "" : "Add a scope, then build lines from your reusable assemblies."}</div>
          </div>
        )}

        {computed?.totals && (
          <div className="border-t border-slate-200 bg-slate-50/60 px-4 py-3" data-testid="cost-type-rollup">
            <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Cost by type</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
              {[["Labor", "labor"], ["Materials", "material"], ["Equipment", "equipment"], ["Travel", "per_diem"], ["Subcontractor", "subcontract"]].map(([lab, k]) => (
                <div key={k} className="rounded-lg border border-slate-200 bg-white px-2.5 py-2" data-testid={`rollup-${k}`}>
                  <div className="text-[11px] text-slate-500 flex items-center gap-1"><span className="w-2 h-2 rounded-full shrink-0" style={{ background: COST_COLORS[k] }} />{lab}</div>
                  <div className="font-mono font-semibold text-slate-800">{money(computed.totals[k] || 0)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <EstimateFooter computed={computed} lines={lines} groupOrder={groupOrder} groups={groups} lineAmt={lineAmt} econOf={econOf} />
      </div>

      <p className="text-[11px] text-slate-400 mt-2">Drag the handle to reorder lines or whole scopes; rename a scope inline. Pick an assembly on any line to pull its cost code and costs in — then tweak them for this bid only. Amounts come straight from the pricing engine.</p>
    </div>
  );
}

// ── Labor / material / equipment / per-diem / sub chips on the scope header: the mix at a glance (lines + what the crew carries). ──
function ScopeMixChips({ group, lines, lineResult, econ }) {
  const sum = (k) => lines.reduce((a, l) => a + (Number(lineResult[l.line_id]?.[k]) || 0), 0);
  const mix = [
    ["Labor", sum("labor_cost"), "labor"], ["Material", sum("material_cost"), "material"],
    ["Equipment", sum("equipment_cost") + (Number(econ?.equipment_cost) || 0), "equipment"],
    ["Per-diem", sum("per_diem_cost") + (Number(econ?.per_diem_cost) || 0), "per_diem"], ["Sub", sum("sub_cost"), "subcontract"],
  ].filter(([, v]) => v > 0);
  if (!mix.length) return null;
  return (
    <span className="hidden lg:flex items-center gap-1" data-testid={`group-mix-${group}`}>
      {mix.map(([lab, v, k]) => (
        <span key={k} className="inline-flex items-center gap-1 text-[11px] font-mono text-slate-600 bg-slate-50 border border-slate-100 rounded px-1.5 py-0.5" data-testid={`group-mix-${group}-${k}`} title={`${lab} in this scope`}>
          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: COST_COLORS[k] || "#94a3b8" }} />{moneyCompact(v)}
        </span>
      ))}
    </span>
  );
}

// ── Rows a scope carries beside its lines: the crew's equipment (one row per machine) and the crew's per-diem.
//    They sit in the scope total, the Rollup table and the itemized export the same way. ──
function ScopeCarriedRows({ group, econ, rowPad, readOnly, onOpen }) {
  const rows = econ?.equipment_rows || [];
  const pd = Number(econ?.per_diem_cost) || 0;
  const cell = "text-sm text-right font-mono font-semibold text-slate-900";
  return (
    <div data-testid={`scope-carried-${group}`}>
      {rows.filter((r) => Number(r.pretax) > 0).map((r, i) => (
        <div key={`${r.name}-${i}`} className={`grid gap-3 px-4 ${rowPad} border-t border-slate-50 items-center bg-amber-50/40`} style={{ gridTemplateColumns: COLS }} data-testid={`scope-equip-row-${group}-${i}`} title={r.derivation?.line || ""}>
          <span />
          <div className="min-w-0 flex items-center gap-2">
            <Truck className="w-3.5 h-3.5 text-amber-600 shrink-0" />
            <span className="text-sm text-slate-800 truncate">{r.name}</span>
            <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${r.ownership === "owned" ? "bg-emerald-50 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{r.ownership}</span>
            <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-medium">crew equipment</span>
          </div>
          <div className="text-[11px] text-slate-400">—</div>
          <div className="text-xs text-slate-500">ea</div>
          <div className="text-xs text-right font-mono text-slate-500">{num(r.qty)}</div>
          <div className="text-xs text-right font-mono text-slate-500">{r.site_days != null ? `${num(r.site_days)} site-d` : "—"}</div>
          <div className="text-xs text-right font-mono text-slate-300">—</div>
          <div className={cell} data-testid={`scope-equip-amount-${group}-${i}`}>{money(r.pretax)}</div>
          <div className="flex items-center justify-end">{!readOnly && <button onClick={onOpen} title="Open crew & equipment" className="text-slate-400 hover:text-[#003E87] p-1" data-testid={`scope-equip-open-${group}-${i}`}><Pencil className="w-3.5 h-3.5" /></button>}</div>
        </div>
      ))}
      {pd > 0 && (
        <div className={`grid gap-3 px-4 ${rowPad} border-t border-slate-50 items-center bg-amber-50/40`} style={{ gridTemplateColumns: COLS }} data-testid={`scope-perdiem-row-${group}`} title={econ?.per_diem_detail || ""}>
          <span />
          <div className="min-w-0 flex items-center gap-2"><Users className="w-3.5 h-3.5 text-amber-600 shrink-0" /><span className="text-sm text-slate-800">Crew per-diem</span><span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-medium">carried by the scope</span></div>
          <div className="text-[11px] text-slate-400">—</div><div className="text-xs text-slate-500">days</div>
          <div className="text-xs text-right font-mono text-slate-500">{econ?.work_days != null ? num(econ.work_days) : "—"}</div>
          <div className="text-xs text-right font-mono text-slate-300">—</div><div className="text-xs text-right font-mono text-slate-300">—</div>
          <div className={cell} data-testid={`scope-perdiem-amount-${group}`}>{money(pd)}</div><span />
        </div>
      )}
    </div>
  );
}

// ── The page's direct work, then every step to the bid total — so the grid foots to the header. ──
function EstimateFooter({ computed, lines, groupOrder, groups, lineAmt, econOf }) {
  const t = computed?.totals;
  const scopeSum = groupOrder.reduce((a, g) => {
    const e = econOf(g);
    return a + (groups[g] || []).reduce((s, l) => s + (lineAmt[l.line_id] || 0), 0) + (Number(e?.equipment_cost) || 0) + (Number(e?.per_diem_cost) || 0);
  }, 0);
  if (!t) {
    return (
      <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-white">
        <span className="text-sm font-semibold text-slate-700">Estimate total</span>
        <span className="text-lg font-mono font-bold text-slate-900" data-testid="grid-grand-total">{scopeSum ? money(scopeSum) : "—"}</span>
      </div>
    );
  }
  const adders = [["Management", t.management], ["General conditions", t.general_conditions], ["Mobilization", t.mobilization], ["Allowances", t.allowances_total]].filter(([, v]) => Number(v) > 0);
  const markups = (Number(t.total) || 0) - (Number(t.direct_cost) || 0);
  const row = (label, value, testid, strong) => (
    <div key={label} className={`flex items-center justify-between px-4 py-1 ${strong ? "font-semibold text-slate-800" : "text-slate-600"}`} data-testid={testid}>
      <span className="text-xs">{label}</span><span className={`font-mono ${strong ? "text-sm" : "text-xs"}`}>{money(value)}</span>
    </div>
  );
  return (
    <div className="border-t border-slate-200 bg-white py-2" data-testid="grid-footer">
      {row("Direct work on this page (scopes, crew equipment, crew per-diem)", scopeSum, "grid-direct-total", true)}
      {adders.map(([lab, v]) => row(`+ ${lab}`, v, `grid-adder-${lab.toLowerCase().replace(/\s+/g, "-")}`))}
      {row("= Direct cost", t.direct_cost, "grid-direct-cost", true)}
      {row("+ Contingency, profit, bond, warranty, tax and fees", markups, "grid-markups")}
      <div className="flex items-center justify-between px-4 pt-2 mt-1 border-t border-slate-100">
        <span className="text-sm font-semibold text-slate-700">Bid total</span>
        <span className="text-lg font-mono font-bold text-slate-900" data-testid="grid-grand-total">{money(t.total)}</span>
      </div>
    </div>
  );
}

// ── A colored subgroup band nested inside a scope group (grid) ──
function SubgroupBand({ group, sub, readOnly, collapsed, onToggle, subtotal, lineCount, onColor, onToggleProposal, onRename, onDelete, onAddLine }) {
  const color = sub.color || DEFAULT_SUBGROUP_COLOR;
  const shown = sub.show !== false;
  return (
    <div className="flex items-center justify-between pl-4 pr-4 py-1.5 border-t border-slate-50"
      style={{ background: tint(color, 0.09), boxShadow: `inset 3px 0 0 ${color}` }}
      data-testid={`subgroup-${group}-${sub.sub}`}>
      <div className="flex items-center gap-2 min-w-0">
        <button onClick={onToggle} title={collapsed ? "Expand subgroup" : "Collapse subgroup"} data-testid={`subgroup-collapse-${group}-${sub.sub}`} className="text-slate-400 hover:text-slate-700 shrink-0">{collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}</button>
        {!readOnly ? (
          <input type="color" value={color} onChange={(e) => onColor(group, sub.sub, e.target.value)} title="Subgroup color"
            data-testid={`subgroup-color-${group}-${sub.sub}`} className="w-5 h-5 rounded cursor-pointer border border-slate-200 bg-transparent p-0 shrink-0" />
        ) : <span className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />}
        {readOnly ? (
          <span className="text-xs font-semibold truncate" style={{ color }}>{sub.sub}</span>
        ) : (
          <input defaultValue={sub.sub} key={sub.sub} onBlur={(e) => onRename(group, sub.sub, e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            data-testid={`subgroup-name-${group}-${sub.sub}`}
            className="text-xs font-semibold bg-transparent border border-transparent hover:border-slate-200 focus:bg-white rounded px-1 py-0.5 max-w-[200px] outline-none" style={{ color }} />
        )}
        <span className="text-[11px] text-slate-400 shrink-0">{lineCount} line{lineCount === 1 ? "" : "s"}</span>
      </div>
      <div className="flex items-center gap-2">
        {!readOnly && (
          <button onClick={() => onToggleProposal(group, sub.sub)} data-testid={`subgroup-proposal-${group}-${sub.sub}`}
            title={shown ? "Shown to client on the proposal — click to hide (its lines roll up into the scope total)" : "Hidden from client — rolls up silently into the scope total"}
            className={`text-[11px] font-medium flex items-center gap-1 rounded px-1.5 py-0.5 ${shown ? "text-slate-500 hover:bg-slate-100" : "text-amber-700 bg-amber-100"}`}>
            {shown ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} {shown ? "On proposal" : "Hidden"}
          </button>
        )}
        {subtotal ? <span className="text-xs font-mono font-semibold text-slate-600" data-testid={`subgroup-subtotal-${group}-${sub.sub}`}>{money(subtotal)}</span> : null}
        {!readOnly && <button onClick={onAddLine} data-testid={`subgroup-add-line-${group}-${sub.sub}`} className="text-[#003E87] hover:bg-[#003E87]/10 rounded px-1.5 py-0.5 text-[11px] font-medium flex items-center gap-1"><Plus className="w-3 h-3" /> line</button>}
        {!readOnly && <button onClick={() => onDelete(group, sub.sub)} data-testid={`subgroup-del-${group}-${sub.sub}`} title="Remove subgroup (lines move back to the scope)" className="text-slate-300 hover:text-rose-600 p-0.5"><Trash2 className="w-3.5 h-3.5" /></button>}
      </div>
    </div>
  );
}

// ── The granular per-line editor ──
function LineEditor({ l, asm, assemblies, codes, codeName, result, readOnly, groupRate, groupCode, subgroups = [], onField, onPickAssembly, onTree, indices = [], project, onReloadAssemblies, kindHint = null }) {
  const q = eff(l);
  const [tree, setTree] = useState(l.takeoff?.assembly_tree || null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [libBusy, setLibBusy] = useState("");   // "" | "new" | "lib" — save-as-new / update-library in-flight guard
  const [libArm, setLibArm] = useState(false);  // 2-step confirm for Update library (no window.confirm → never blocks)
  const [naming, setNaming] = useState(false);  // inline "save as new" name entry (replaces window.prompt)
  const [newName, setNewName] = useState("");
  // Live display updates instantly via setTree; the updateBid persist is debounced so a long
  // numeric edit doesn't fire one PATCH per keystroke. Pending save flushes on unmount/collapse.
  const treeSaveRef = useRef(null);
  const latestTree = useRef(null);
  const onTreeRef = useRef(onTree);
  onTreeRef.current = onTree;
  const handleTreeChange = (t) => {
    setTree(t); latestTree.current = t;
    if (treeSaveRef.current) clearTimeout(treeSaveRef.current);
    treeSaveRef.current = setTimeout(() => { treeSaveRef.current = null; onTreeRef.current && onTreeRef.current(t); }, 350);
  };
  useEffect(() => () => { if (treeSaveRef.current) { clearTimeout(treeSaveRef.current); if (latestTree.current && onTreeRef.current) onTreeRef.current(latestTree.current); } }, []);
  useEffect(() => {
    if (l.takeoff?.assembly_tree) { setTree(l.takeoff.assembly_tree); return; }
    if (l.assembly_id) { setTreeLoading(true); api.get(`/assemblies/${l.assembly_id}/resolved`).then((r) => setTree(r.data)).catch(() => setTree(null)).finally(() => setTreeLoading(false)); }
    else setTree(null);
  }, [l.assembly_id, l.takeoff?.assembly_tree]); // eslint-disable-line react-hooks/exhaustive-deps
  const startSaveAsNew = () => { if (libBusy) return; setNewName(`${tree?.name || l.name} (custom)`); setNaming(true); };
  const confirmSaveAsNew = async () => {
    const name = (newName || "").trim();
    if (!name) { toast.error("Name the new assembly."); return; }
    if ((assemblies || []).some((a) => (a.name || "").trim().toLowerCase() === name.toLowerCase())) { toast.error(`"${name}" already exists in your library — pick another name.`); return; }
    setLibBusy("new");
    try { await api.post("/assemblies", { name, measure_type: tree.measure_type, per_unit: tree.per_unit, time_basis: tree.time_basis, cost_code: tree.cost_code, components: cleanTreeNodes(tree.components), source: { kind: "estimate-drill", ...(project || {}) } }); toast.success(`Saved "${name}" to your library`); setNaming(false); onReloadAssemblies && onReloadAssemblies(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save assembly"); }
    finally { setLibBusy(""); }
  };
  const updateLibrary = async () => {
    if (!tree?.assembly_id || libBusy) return;
    if (!libArm) { setLibArm(true); setTimeout(() => setLibArm(false), 4000); return; }
    setLibArm(false); setLibBusy("lib");
    try { await api.post("/assemblies", { assembly_id: tree.assembly_id, name: tree.name, measure_type: tree.measure_type, per_unit: tree.per_unit, time_basis: tree.time_basis, cost_code: tree.cost_code, components: cleanTreeNodes(tree.components) }); toast.success("Library assembly updated"); onReloadAssemblies && onReloadAssemblies(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update library"); }
    finally { setLibBusy(""); }
  };
  const costFields = [
    ["unit_material_cost", "Material $/unit", asm ? "from assembly" : ""],
    ["unit_equipment_cost", "Equipment $/unit", ""],
    ["unit_travel_cost", "Travel $/unit", ""],
  ];
  return (
    <div className="px-5 py-4 bg-[#003E87]/[0.03] border-t border-[#003E87]/10" data-testid={`editor-${l.line_id}`}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: identity */}
        <div className="space-y-3">
          <div>
            <span className="text-[11px] uppercase tracking-wide text-slate-500">Built from assembly</span>
            <div className="mt-1"><AssemblyPicker assemblies={assemblies} value={l.assembly_id} onPick={onPickAssembly} /></div>
            {asm && <p className="text-[11px] text-slate-400 mt-1">Pulled this assembly's cost code + costs. Edits below apply to this bid only — your saved assembly is untouched.</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-slate-500">Cost code {(asm || groupCode) ? "(override)" : ""}</span>
              <CostCodeCombobox disabled={readOnly} value={l.cost_code || ""} onChange={(v) => onField("cost_code", v)}
                testid={`cc-${l.line_id}`} inherit={groupCode ? `inherit scope ${groupCode}` : "— none —"}
                className="w-full mt-1 text-xs font-mono" />
              {l.cost_code && codeName(l.cost_code) && <span className="text-[11px] text-slate-400">{codeName(l.cost_code)}</span>}
              {!l.cost_code && groupCode && <span className="block text-[11px] text-[#003E87]/60">inherits scope {groupCode}</span>}
              {asm && !asm.cost_code && !l.cost_code && !groupCode && <span className="block text-[11px] text-amber-600">This assembly has no cost code — set one here or on the scope.</span>}
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-slate-500">Unit of measure</span>
              <input disabled={readOnly} value={l.unit || ""} onChange={(e) => onField("unit", e.target.value)}
                data-testid={`uom-${l.line_id}`} placeholder="ea"
                className="w-full mt-1 text-xs border border-slate-200 rounded-lg px-2.5 py-2 focus:border-[#003E87] outline-none" />
            </label>
          </div>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">Discipline (optional)</span>
            <select disabled={readOnly} value={l.discipline || ""} onChange={(e) => onField("discipline", e.target.value)}
              data-testid={`line-discipline-${l.line_id}`}
              className="w-full mt-1 text-xs border border-slate-200 rounded-lg px-2.5 py-2 focus:border-[#003E87] outline-none bg-white">
              <option value="">— none —</option>
              {DISCIPLINES.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </label>
          {subgroups.length > 0 && (
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-slate-500">Subgroup</span>
              <select disabled={readOnly} value={l.subgroup || ""} onChange={(e) => onField("subgroup", e.target.value || null)}
                data-testid={`line-subgroup-${l.line_id}`}
                className="w-full mt-1 text-xs border border-slate-200 rounded-lg px-2.5 py-2 focus:border-[#003E87] outline-none bg-white">
                <option value="">— none —</option>
                {subgroups.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          )}
          <div className="grid grid-cols-3 gap-3">
            {[["multiplier", "× Multiplier"], ["waste_pct", "Waste %"], ["difficulty", "Difficulty ×"]].map(([f, lab]) => (
              <label key={f} className="block">
                <span className="text-[11px] uppercase tracking-wide text-slate-500">{lab}</span>
                <input type="number" step="any" disabled={readOnly} value={l[f] === 0 || l[f] == null ? (f === "multiplier" || f === "difficulty" ? (l[f] ?? "") : "") : l[f]} onChange={(e) => onField(f, e.target.value)}
                  data-testid={`${f}-${l.line_id}`} placeholder={f === "multiplier" || f === "difficulty" ? "1" : "0"}
                  className="w-full mt-1 text-xs border border-slate-200 rounded-lg px-2.5 py-2 text-right font-mono focus:border-[#003E87] outline-none" />
              </label>
            ))}
          </div>
          <ProvenanceFields basis={l.basis} grade={l.grade} onField={onField} readOnly={readOnly} idSuffix={l.line_id} />
          <LineKindFields line={l} onField={onField} readOnly={readOnly} hint={kindHint} />
        </div>

        {/* Right: costs */}
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">Costs (per unit) · total qty {num(q)}</span>
            <div className="flex items-center gap-1.5">
              <button type="button" disabled={readOnly} onClick={() => onField("sub_supplied", !l.sub_supplied)} data-testid={`sub-supplied-${l.line_id}`}
                className={`text-[11px] px-2 py-1 rounded-lg font-medium border transition-colors ${l.sub_supplied ? "bg-amber-100 text-amber-700 border-amber-300" : "text-slate-500 border-slate-200 hover:bg-white"}`}>
                {l.sub_supplied ? "✓ Sub-supplied (price only)" : "Sub-supplied"}
              </button>
              <button type="button" disabled={readOnly} onClick={() => onField("is_subcontract", !l.is_subcontract)} data-testid={`sub-${l.line_id}`}
                className={`text-[11px] px-2 py-1 rounded-lg font-medium border transition-colors ${l.is_subcontract ? "bg-violet-100 text-violet-700 border-violet-300" : "text-slate-500 border-slate-200 hover:bg-white"}`}>
                {l.is_subcontract ? "✓ Subcontracted line" : "Mark as subcontract"}
              </button>
            </div>
          </div>
          {l.sub_supplied && (
            <div className="grid grid-cols-2 gap-3" data-testid={`sub-supplied-fields-${l.line_id}`}>
              <label className="block"><span className="text-[11px] text-slate-500">Brand</span>
                <input disabled={readOnly} value={l.brand || ""} onChange={(e) => onField("brand", e.target.value)} data-testid={`brand-${l.line_id}`} placeholder="e.g. Nucor, Unirac"
                  className="w-full mt-0.5 text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:border-[#003E87] outline-none bg-white" /></label>
              <label className="block"><span className="text-[11px] text-slate-500">Supplier</span>
                <input disabled={readOnly} value={l.supplier || ""} onChange={(e) => onField("supplier", e.target.value)} data-testid={`supplier-${l.line_id}`} placeholder="e.g. Border States"
                  className="w-full mt-0.5 text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:border-[#003E87] outline-none bg-white" /></label>
              <p className="col-span-2 text-[11px] text-amber-600">Subcontractor-supplied — labor hours are skipped. Capture the quoted price below (Material or Sub $/unit).</p>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {!l.sub_supplied && (
              <label className="block">
                <span className="text-[11px] text-slate-500">Labor $/hr {groupRate ? "(override)" : ""}</span>
                <input type="number" step="any" disabled={readOnly} value={l.labor_rate === 0 || l.labor_rate == null ? "" : l.labor_rate} onChange={(e) => onField("labor_rate", e.target.value)}
                  data-testid={`labor_rate-${l.line_id}`} placeholder={groupRate ? `crew ${num(groupRate)}` : "0"}
                  className="w-full mt-0.5 text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-right font-mono focus:border-[#003E87] outline-none bg-white" />
                {groupRate ? <span className="text-[11px] text-[#003E87]/60">inherits crew ${num(groupRate)}/hr</span> : null}
              </label>
            )}
            {!l.sub_supplied && (
              <label className="block">
                <span className="text-[11px] text-slate-500">Labor time/unit {asm ? (l.labor_basis === "crew" ? "(elapsed × crew)" : "(from assembly)") : ""}</span>
                <LaborHoursInput disabled={readOnly} value={l.unit_labor_hours === 0 || l.unit_labor_hours == null ? "" : l.unit_labor_hours}
                  onChange={(v) => onField("unit_labor_hours", v)} testid={`unit_labor_hours-${l.line_id}`}
                  className="mt-0.5 text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-right font-mono focus:border-[#003E87] outline-none bg-white" />
              </label>
            )}
            {!l.sub_supplied && groupRate && l.unit_labor_cost != null && l.unit_labor_cost !== "" && Number(l.unit_labor_cost) !== 0 ? (
              <div className="col-span-2 sm:col-span-3 text-[11px] text-amber-600 -mt-1" data-testid={`crew-ignored-${l.line_id}`}>
                This line has its own ${num(l.unit_labor_cost)}/unit labor, so it ignores the crew rate.
                {!readOnly && <button type="button" onClick={() => onField("unit_labor_cost", "")} className="ml-1 underline hover:text-amber-700">Clear to inherit crew</button>}
              </div>
            ) : null}
            {costFields.map(([f, lab, hint]) => (
              <label key={f} className="block">
                <span className="text-[11px] text-slate-500">{lab}</span>
                <input type="number" step="any" disabled={readOnly} value={l[f] === 0 || l[f] == null ? "" : l[f]} onChange={(e) => onField(f, e.target.value)}
                  data-testid={`${f}-${l.line_id}`} placeholder="0"
                  className="w-full mt-0.5 text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-right font-mono focus:border-[#003E87] outline-none bg-white" />
                {hint && <span className="text-[11px] text-[#003E87]/60">{hint}</span>}
              </label>
            ))}
            <label className="block">
              <span className="text-[11px] text-slate-500">Sub $/unit</span>
              <input type="number" step="any" disabled={readOnly} value={l.unit_sub_cost === 0 || l.unit_sub_cost == null ? "" : l.unit_sub_cost} onChange={(e) => onField("unit_sub_cost", e.target.value)}
                data-testid={`unit_sub_cost-${l.line_id}`} placeholder="0"
                className="w-full mt-0.5 text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-right font-mono focus:border-[#003E87] outline-none bg-white" />
            </label>
          </div>
          {result && result.status === "priced" && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1" data-testid={`breakdown-${l.line_id}`}>
              {[["Labor", result.labor_cost, "labor"], ["Material", result.material_cost, "material"], ["Equip", result.equipment_cost, "equipment"], ["Travel", result.per_diem_cost, "per_diem"]].map(([lab, v, k]) => (
                <div key={lab} className="text-[11px] flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: COST_COLORS[k] }} /><span className="text-slate-400">{lab} </span><span className="font-mono text-slate-700">{money(v || 0)}</span></div>
              ))}
              {result.man_hours ? <div className="text-[11px]"><span className="text-slate-400">Man-hrs </span><span className="font-mono text-slate-700">{num(result.man_hours)}</span></div> : null}
            </div>
          )}
          {result && result.status === "missing" && (
            <div className="text-[11px] text-amber-600" data-testid={`missing-${l.line_id}`}>Not priced yet — enter a quantity and costs (or pick an assembly) to price this line.</div>
          )}
        </div>
      </div>
      {(l.assembly_id || l.takeoff?.assembly_tree) && (
        <div className="mt-4 pt-3 border-t border-[#003E87]/10" data-testid={`line-components-${l.line_id}`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1"><Boxes className="w-3.5 h-3.5 text-[#003E87]" /> Assembly components — edit for this bid</span>
            {!readOnly && tree && (
              <div className="flex items-center gap-1.5">
                {naming ? (
                  <>
                    <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") confirmSaveAsNew(); if (e.key === "Escape") setNaming(false); }} placeholder="New assembly name" data-testid={`line-new-name-${l.line_id}`} className="text-[11px] border border-slate-200 rounded px-2 py-0.5 w-44 focus:border-[#003E87] outline-none" />
                    <button type="button" onClick={confirmSaveAsNew} disabled={!!libBusy} className="text-[11px] px-2 py-0.5 rounded bg-[#003E87] text-white disabled:opacity-50" data-testid={`line-save-confirm-${l.line_id}`}>{libBusy === "new" ? "Saving…" : "Save"}</button>
                    <button type="button" onClick={() => setNaming(false)} className="text-[11px] px-1.5 py-0.5 text-slate-400 hover:text-slate-700">Cancel</button>
                  </>
                ) : (
                  <>
                    <button type="button" onClick={startSaveAsNew} disabled={!!libBusy} className="text-[11px] px-2 py-0.5 rounded border border-[#003E87] text-[#003E87] hover:bg-[#003E87]/5 disabled:opacity-50" data-testid={`line-save-as-new-${l.line_id}`}>Save as new</button>
                    {tree.assembly_id && <button type="button" onClick={updateLibrary} disabled={!!libBusy} className={`text-[11px] px-2 py-0.5 rounded border disabled:opacity-50 ${libArm ? "border-amber-500 bg-amber-100 text-amber-800 font-semibold" : "border-amber-300 text-amber-700 hover:bg-amber-50"}`} data-testid={`line-update-library-${l.line_id}`}>{libBusy === "lib" ? "Updating…" : libArm ? "Click to overwrite" : "Update library"}</button>}
                  </>
                )}
              </div>
            )}
          </div>
          {treeLoading ? <div className="text-xs text-slate-400 py-3 text-center">Loading components…</div>
            : tree ? <AssemblyDrilldown tree={tree} editable={!readOnly} indices={indices} onChange={handleTreeChange} />
            : <div className="text-xs text-slate-400 py-2">No component breakdown for this line.</div>}
          <div className="text-[11px] text-slate-400 mt-1.5">Edits apply to this bid only — the library assembly stays untouched. Changes sync with the takeoff page.</div>
        </div>
      )}
    </div>
  );
}

// ── Configurable per-bid burden override: an editable list of named burden lines ──
function BurdenOverrideDialog({ open, onClose, group, appliedLines, onSave }) {
  const [lines, setLines] = useState([]);
  useEffect(() => {
    if (open) setLines((appliedLines || []).map((x) => ({ name: x.name, pct: x.pct })));
  }, [open, appliedLines]);
  const total = lines.reduce((s, x) => s + (Number(x.pct) || 0), 0);
  const setLine = (i, f, v) => setLines((ls) => ls.map((x, j) => (j === i ? { ...x, [f]: f === "pct" ? (v === "" ? "" : Number(v)) : v } : x)));
  const addLine = () => setLines((ls) => [...ls, { name: "", pct: "" }]);
  const delLine = (i) => setLines((ls) => ls.filter((_, j) => j !== i));
  const save = () => {
    const clean = lines
      .filter((x) => (x.name || "").trim() || (x.pct !== "" && x.pct != null))
      .map((x) => ({ name: (x.name || "Burden").trim(), pct: Number(x.pct) || 0 }));
    onSave({ mode: "override", lines: clean });
    onClose();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid={`burden-dialog-${group}`}>
        <DialogHeader>
          <DialogTitle>Bid-specific labor burden</DialogTitle>
          <DialogDescription>These burden lines apply to <b>{group}</b> on <b>this bid only</b>, overriding the company default. Add, rename or remove any line you need.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-1">
          {lines.length === 0 && <div className="text-xs text-slate-400 italic">No burden lines — this bid would carry 0% burden. Add a line to change that.</div>}
          {lines.map((x, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`burden-dialog-line-${i}`}>
              <input value={x.name ?? ""} onChange={(e) => setLine(i, "name", e.target.value)} placeholder="Burden name (e.g. SUTA)" data-testid={`burden-dialog-name-${i}`}
                className="flex-1 text-sm border border-slate-200 rounded px-2 py-1.5 focus:border-[#003E87] outline-none" />
              <div className="relative w-24">
                <input type="number" step="0.01" value={x.pct ?? ""} onChange={(e) => setLine(i, "pct", e.target.value)} placeholder="0.00" data-testid={`burden-dialog-pct-${i}`}
                  className="w-full text-sm border border-slate-200 rounded px-2 py-1.5 pr-5 text-right font-mono focus:border-[#003E87] outline-none" />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
              </div>
              <button onClick={() => delLine(i)} data-testid={`burden-dialog-remove-${i}`} className="text-slate-300 hover:text-rose-600 p-1"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={addLine} data-testid="burden-dialog-add" className="text-xs text-[#003E87] hover:underline flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add burden line</button>
          <div className="flex items-center justify-between border-t border-slate-100 pt-2 mt-1 text-sm">
            <span className="text-slate-500">Total burden on base wage</span>
            <span className="font-mono font-bold text-slate-900" data-testid="burden-dialog-total">{total.toFixed(2)}%</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="burden-dialog-cancel">Cancel</Button>
          <Button size="sm" className="bg-[#003E87] hover:bg-[#00306a] text-white" onClick={save} data-testid="burden-dialog-save">Save override</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Combined movable Crew + Equipment popup. Labor is straight man-hours × blended rate
// (no slowdown premium, no production/day). Duration = man-hours ÷ (headcount × hours/day),
// stretched by weather + site slowdowns; compression happens on the Schedule page. Enforces
// the equipment→operator rule and the Commercial-Terms apprentice-ratio minimum. ──
function CrewEquipPanel({ group, crew, equipment, econ, readOnly, onSave, onClose, library = [], onSaveToLibrary, weather, bidBurden, onBidBurden, projHours = 10, apprenticeshipPct = 0, equipCatalog = [], equipCategories = [], location, workDays, onSaveToCatalog }) {
  const [members, setMembers] = useState(crew?.members?.length
    ? crew.members.map((m) => ({ is_operator: false, is_apprentice: false, ...m }))
    : [{ role: "", count: 1, base_rate: "", fringe: "", per_diem: "", is_operator: false, is_apprentice: false }]);
  const [equipList, setEquipList] = useState(equipment || []);
  const setM = (i, f, v) => setMembers((ms) => ms.map((m, j) => (j === i ? { ...m, [f]: v } : m)));
  const addM = () => setMembers((ms) => [...ms, { role: "", count: 1, base_rate: "", fringe: "", per_diem: "", is_operator: false, is_apprentice: false }]);
  const delM = (i) => setMembers((ms) => ms.filter((_, j) => j !== i));

  const otf = econ?.ot_factor ?? 1;
  const burdenPct = econ?.burden_pct ?? 0;
  const [showWx, setShowWx] = useState(false);
  const [showBurdenDlg, setShowBurdenDlg] = useState(false);
  const MONTHS3 = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];

  const preview = (() => {
    const up = otf * (1 + burdenPct / 100);
    let hc = 0, crewHourly = 0, pdDaily = 0, apprHc = 0, operators = 0;
    members.forEach((m) => {
      const c = Number(m.count) || 0;
      hc += c; crewHourly += c * (((Number(m.base_rate) || 0) * up) + (Number(m.fringe) || 0)); pdDaily += c * (Number(m.per_diem) || 0);
      if (m.is_apprentice) apprHc += c;
      if (m.is_operator && c > 0) operators += c;
    });
    const blended = hc ? crewHourly / hc : 0;
    return { hc, crewHourly, daily: crewHourly * (Number(projHours) || 10), blended, pdDaily, blendedPd: hc ? pdDaily / hc : 0, apprHc, operators, apprPct: hc ? (apprHc / hc) * 100 : 0 };
  })();

  const hasEquip = (equipList || []).some((x) => (x.name || "").trim());
  const apprMin = Number(apprenticeshipPct) || 0;
  const operatorViolation = hasEquip && preview.operators < 1;
  const apprenticeViolation = apprMin > 0 && preview.apprPct + 1e-6 < apprMin;
  const blocked = operatorViolation || apprenticeViolation;

  const buildCrew = () => ({
    members: members.filter((m) => m.role || m.base_rate).map((m) => ({ role: m.role || "Worker", count: Number(m.count) || 0, base_rate: Number(m.base_rate) || 0, fringe: Number(m.fringe) || 0, per_diem: Number(m.per_diem) || 0, is_operator: !!m.is_operator, is_apprentice: !!m.is_apprentice })),
    days_override: crew?.days_override, target_work_days: crew?.target_work_days, lane: crew?.lane,
  });
  const cleanEquip = () => (equipList || []).filter((x) => (x.name || "").trim()).map((x) => ({ ...x, qty: Number(x.qty) || 1 }));
  const loadFromLib = (id) => {
    const c = library.find((x) => x.id === id)?.crew; if (!c) return;
    setMembers((c.members || []).map((m) => ({ is_operator: false, is_apprentice: false, ...m })));
  };
  const doSave = (clear) => {
    if (clear) { onSave({ crew: { members: [] }, equipment: cleanEquip() }); onClose(); return; }
    if (blocked) { toast.error(operatorViolation ? "Add at least one operator — this crew has equipment." : `Apprentices must be at least ${apprMin}% of the crew.`); return; }
    const eq = cleanEquip();
    onSave({ crew: buildCrew(), equipment: eq });
    if (!readOnly && onSaveToCatalog) eq.forEach((it) => onSaveToCatalog(it, true));
    onClose();
  };

  const siteSlowPct = (econ?.weather_factor && econ?.productivity != null)
    ? Math.max(0, Math.round((1 - econ.productivity / econ.weather_factor) * 100)) : 0;

  return (
    <DraggablePanel storageKey={`crewequip:${group}`} className="w-[min(820px,95vw)]">
      <div className="rounded-xl border border-slate-200 bg-white shadow-2xl overflow-hidden flex flex-col max-h-[86vh]" data-testid={`crew-panel-${group}`}>
        <DragHandle className="flex items-center justify-between px-4 py-2.5 bg-[#003E87] text-white">
          <div className="flex items-center gap-2 text-sm font-semibold"><GripHorizontal className="w-4 h-4 opacity-70" /> Crew &amp; Equipment — “{group}”</div>
          <button onClick={onClose} data-no-drag data-testid={`crew-close-${group}`} className="text-white/80 hover:text-white"><X className="w-4 h-4" /></button>
        </DragHandle>

        <div className="overflow-auto px-5 py-4 space-y-4">
          {/* Summary strip */}
          <div className="flex items-center gap-3 flex-wrap text-[11px]">
            <span className="text-slate-500">Headcount <b className="text-slate-800">{preview.hc}</b></span>
            <span className="text-slate-500">Blended <b className="text-slate-800">${num(preview.blended)}/hr</b></span>
            {preview.pdDaily > 0 && <span className="text-slate-500" data-testid={`crew-blended-perdiem-${group}`}>Per-diem <b className="text-slate-800">${num(preview.blendedPd)}/day</b></span>}
            <span className="text-slate-500">Crew/day <b className="text-slate-800">{money(preview.daily)}</b></span>
            {econ?.man_hours ? <span className="text-slate-500">Man-hrs <b className="text-slate-800">{num(econ.man_hours)}</b></span> : null}
            {econ?.days != null ? <span className="text-slate-500" data-testid={`crew-duration-${group}`}>Duration <b className="text-slate-800">{num(econ.days)} work-days</b>{econ.crews > 1 ? ` · ${num(econ.crews)}× crew` : ""}</span> : null}
          </div>

          {!readOnly && library.length > 0 && (
            <div className="flex items-center gap-2" data-testid={`crew-lib-bar-${group}`}>
              <span className="text-[11px] text-slate-400">Load a saved crew:</span>
              <select data-testid={`crew-lib-load-${group}`} defaultValue="" onChange={(e) => { loadFromLib(e.target.value); e.target.value = ""; }}
                className="text-[11px] border border-slate-200 rounded px-2 py-1 bg-white">
                <option value="">Pick from library…</option>
                {library.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            </div>
          )}

          {/* Members table */}
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="grid gap-2 px-3 py-1.5 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-400" style={{ gridTemplateColumns: "minmax(0,1.3fr) 44px 72px 72px 84px 96px 74px 24px" }}>
              <div>Role</div><div className="text-right">Count</div><div className="text-right">Rate $/hr</div><div className="text-right">Fringe $/hr</div><div className="text-right">Per-diem</div><div className="text-center">Role flags</div><div className="text-right">Burdened</div><div />
            </div>
            {members.map((m, i) => (
              <div key={i} className="grid gap-2 px-3 py-1.5 border-t border-slate-50 items-center" style={{ gridTemplateColumns: "minmax(0,1.3fr) 44px 72px 72px 84px 96px 74px 24px" }}>
                <input disabled={readOnly} value={m.role || ""} onChange={(e) => setM(i, "role", e.target.value)} placeholder="e.g. Operator" data-testid={`crew-member-role-${group}-${i}`}
                  className="text-xs border border-slate-200 rounded px-2 py-1 focus:border-[#003E87] outline-none" />
                <input type="number" disabled={readOnly} value={m.count ?? ""} onChange={(e) => setM(i, "count", e.target.value)} data-testid={`crew-member-count-${group}-${i}`}
                  className="text-xs border border-slate-200 rounded px-2 py-1 text-right font-mono focus:border-[#003E87] outline-none" />
                <input type="number" step="any" disabled={readOnly} value={m.base_rate ?? ""} onChange={(e) => setM(i, "base_rate", e.target.value)} placeholder="0" data-testid={`crew-member-rate-${group}-${i}`}
                  className="text-xs border border-slate-200 rounded px-2 py-1 text-right font-mono focus:border-[#003E87] outline-none" />
                <input type="number" step="any" disabled={readOnly} value={m.fringe ?? ""} onChange={(e) => setM(i, "fringe", e.target.value)} placeholder="0" data-testid={`crew-member-fringe-${group}-${i}`}
                  className="text-xs border border-slate-200 rounded px-2 py-1 text-right font-mono focus:border-[#003E87] outline-none" />
                <input type="number" step="any" disabled={readOnly} value={m.per_diem ?? ""} onChange={(e) => setM(i, "per_diem", e.target.value)} placeholder="0" data-testid={`crew-member-perdiem-${group}-${i}`}
                  className="text-xs border border-slate-200 rounded px-2 py-1 text-right font-mono focus:border-[#003E87] outline-none" />
                <div className="flex items-center justify-center gap-1">
                  <button type="button" disabled={readOnly} onClick={() => setM(i, "is_operator", !m.is_operator)} data-testid={`crew-member-operator-${group}-${i}`} title="Equipment operator"
                    className={`text-[11px] px-1.5 py-0.5 rounded border font-medium ${m.is_operator ? "bg-[#003E87] text-white border-[#003E87]" : "text-slate-400 border-slate-200 hover:bg-slate-50"}`}>Op</button>
                  <button type="button" disabled={readOnly} onClick={() => setM(i, "is_apprentice", !m.is_apprentice)} data-testid={`crew-member-apprentice-${group}-${i}`} title="Apprentice"
                    className={`text-[11px] px-1.5 py-0.5 rounded border font-medium ${m.is_apprentice ? "bg-[#F4C400] text-black border-[#F4C400]" : "text-slate-400 border-slate-200 hover:bg-slate-50"}`}>Appr</button>
                </div>
                <div className="text-xs text-right font-mono text-slate-600">${num(((Number(m.base_rate) || 0) * otf * (1 + burdenPct / 100)) + (Number(m.fringe) || 0))}</div>
                {!readOnly && <button onClick={() => delM(i)} className="text-slate-300 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>}
              </div>
            ))}
            {!readOnly && <button onClick={addM} data-testid={`crew-add-member-${group}`} className="w-full text-[11px] text-[#003E87] hover:bg-[#003E87]/5 py-1.5 flex items-center justify-center gap-1"><Plus className="w-3 h-3" /> add member</button>}
          </div>

          {/* Compliance: operator + apprentice enforcement */}
          {(hasEquip || apprMin > 0) && (
            <div className={`rounded-lg border px-3 py-2 text-[11px] space-y-1 ${blocked ? "border-rose-200 bg-rose-50" : "border-emerald-200 bg-emerald-50"}`} data-testid={`crew-compliance-${group}`}>
              {hasEquip && (
                <div className={`flex items-center gap-1.5 ${operatorViolation ? "text-rose-700" : "text-emerald-700"}`} data-testid={`crew-operator-check-${group}`}>
                  {operatorViolation ? <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> : <span className="font-bold">✓</span>}
                  {operatorViolation ? "Equipment is on this crew — flag at least one member as an Operator." : `Operator requirement met (${preview.operators} operator${preview.operators === 1 ? "" : "s"}).`}
                </div>
              )}
              {apprMin > 0 && (
                <div className={`flex items-center gap-1.5 ${apprenticeViolation ? "text-rose-700" : "text-emerald-700"}`} data-testid={`crew-apprentice-check-${group}`}>
                  {apprenticeViolation ? <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> : <span className="font-bold">✓</span>}
                  Apprentices {preview.apprPct.toFixed(0)}% of crew · required minimum {apprMin.toFixed(0)}%{apprenticeViolation ? " — flag more members as Apprentice." : "."}
                </div>
              )}
            </div>
          )}

          {/* Rate build-up */}
          <div className="rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2 text-[11px] text-slate-600 space-y-1" data-testid={`crew-rate-breakdown-${group}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="uppercase tracking-wide text-[11px] text-slate-400">How this rate is built</span>
              <span className="px-1.5 py-0.5 rounded bg-white border border-slate-200" data-testid={`crew-applied-ot-${group}`}>OT ×{otf.toFixed(2)} <span className="text-slate-400">· auto from {projHours}h × {econ?.days_per_week ?? 5}d week (FLSA &gt;40h)</span></span>
              <span className="px-1.5 py-0.5 rounded bg-white border border-slate-200" data-testid={`crew-hours-note-${group}`}>Hours/day {projHours} <span className="text-slate-400">· project</span></span>
              <span className="px-1.5 py-0.5 rounded bg-white border border-slate-200" data-testid={`crew-applied-burden-${group}`}>Burden {burdenPct.toFixed(2)}% <span className="text-slate-400">· {bidBurden ? "bid override" : "company default"}</span>
                {!readOnly && <button onClick={() => setShowBurdenDlg(true)} className="text-[#003E87] hover:underline ml-1" data-testid={`crew-burden-override-${group}`}>{bidBurden ? "edit" : "override"}</button>}
                {!readOnly && bidBurden && <button onClick={() => onBidBurden && onBidBurden(null)} className="text-slate-400 hover:text-rose-600 ml-1" data-testid={`crew-burden-clear-${group}`}>clear</button>}
              </span>
            </div>
            <div className="font-mono text-slate-500">base $/hr × {otf.toFixed(2)} OT × {(1 + burdenPct / 100).toFixed(3)} burden + fringe (flat) = blended ${num(preview.blended)}/hr</div>
            <div className="font-mono text-slate-500">labor = man-hours × ${num(preview.blended)}/hr · duration = man-hours ÷ ({preview.hc || 0} × {projHours}h/day{econ?.productivity != null ? ` × ${Math.round((econ.productivity) * 100)}% productivity` : ""})</div>
            {preview.pdDaily > 0 && <div className="font-mono text-slate-500" data-testid={`crew-perdiem-breakdown-${group}`}>per-diem = ${num(preview.pdDaily)}/day for the crew ÷ {preview.hc} = ${num(preview.blendedPd)}/day per person (headcount-weighted)</div>}
            {econ?.productivity != null && (
              <div className="font-mono text-slate-500">schedule productivity {Math.round(econ.productivity * 100)}% = weather{" "}
                <button onClick={() => setShowWx((s) => !s)} className="text-[#003E87] hover:underline font-sans" data-testid={`crew-weather-toggle-${group}`}>{Math.round((econ.weather_factor ?? 1) * 100)}%</button>{" "}
                × (1 − site slowdown {siteSlowPct}%) · compress on the Schedule page</div>
            )}
            {showWx && (
              <div className="mt-1 rounded border border-slate-200 bg-white p-2" data-testid={`crew-weather-panel-${group}`}>
                <div className="text-[11px] text-slate-500 mb-1">Field-days lost per month{weather?.county ? ` · ${weather.county}` : ""} — this is what drives the {Math.round((econ.weather_factor ?? 1) * 100)}% weather factor.</div>
                {weather?.months?.length ? (() => {
                  const mx = Math.max(...weather.months.map((x) => x.lost_days || 0), 1);
                  const MONTHS_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
                  const CCOLOR = { Cold: "#2563EB", Heat: "#DC2626", Wet: "#0D9488", Wind: "#64748B", Mild: "#CA8A04", Normal: "#CA8A04" };
                  return (
                    <div className="flex items-end gap-1 h-24" role="img" aria-label="Field-days lost by month — taller bars mean more weather delay; color shows the dominant driver">
                      {weather.months.map((mo) => {
                        const v = mo.lost_days || 0;
                        const isPeak = v > 0 && v >= mx - 1e-9;
                        const col = CCOLOR[mo.condition] || "#003E87";
                        return (
                          <div key={mo.month} className="flex-1 flex flex-col items-center justify-end"
                            aria-label={`${MONTHS_FULL[mo.month - 1]}: ${v.toFixed(1)} field-days lost${mo.driver_label ? ` (${mo.driver_label})` : ""}`}
                            title={`${MONTHS_FULL[mo.month - 1]}: ${v.toFixed(1)} field-days lost${mo.driver_label ? ` — ${mo.driver_label}` : ""}`}>
                            <span className={`text-[8px] font-mono mb-0.5 ${isPeak ? "font-bold" : "text-slate-400"}`} style={isPeak ? { color: col } : undefined} data-testid={`crew-weather-bar-label-${group}-${mo.month}`}>{v ? v.toFixed(1) : ""}</span>
                            <div className="w-full rounded-t" style={{ height: `${(v / mx) * 72}%`, minHeight: v ? "2px" : "0", background: col, opacity: isPeak ? 1 : 0.65 }} data-testid={`crew-weather-bar-${group}-${mo.month}`} />
                            <span className={`text-[8px] mt-0.5 ${isPeak ? "font-semibold" : "text-slate-400"}`} style={isPeak ? { color: col } : undefined}>{MONTHS3[mo.month - 1]}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })() : <div className="text-[11px] text-slate-400">No weather model yet — set the site's state &amp; county under Productivity.</div>}
                <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-1.5" data-testid={`crew-weather-legend-${group}`}>
                  {[["Cold", "#2563EB", "cold"], ["Heat", "#DC2626", "heat"], ["Wet", "#0D9488", "rain"], ["Wind", "#64748B", "wind"], ["Mild", "#CA8A04", "mild"]].map(([k, c, lbl]) => (
                    <span key={k} className="flex items-center gap-1 text-[8px] text-slate-400"><span className="w-2 h-2 rounded-sm" style={{ background: c }} /> {lbl}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Equipment (linked to the crew) */}
          <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid={`crew-equip-section-${group}`}>
            <div className="px-3 py-2 bg-[#003E87]/[0.04] border-b border-slate-100 text-xs font-semibold text-slate-700 flex items-center gap-2"><Truck className="w-4 h-4 text-[#003E87]" /> Equipment {hasEquip ? "" : "(optional)"}</div>
            <div className="p-2">
              <EquipmentPanel embedded group={group} items={equipment} econRows={econ?.equipment_rows} readOnly={readOnly}
                catalog={equipCatalog} categories={equipCategories} location={location}
                workDays={workDays} projHours={projHours}
                onSave={() => {}} onListChange={setEquipList} onSaveToCatalog={onSaveToCatalog} onClose={() => {}} />
            </div>
          </div>
        </div>

        <BurdenOverrideDialog open={showBurdenDlg} onClose={() => setShowBurdenDlg(false)} group={group}
          appliedLines={econ?.burden_lines || []} onSave={(lb) => onBidBurden && onBidBurden(lb)} />

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50">
          <p className="text-[11px] text-slate-400 max-w-sm">Labor = each line's man-hours × the blended rate. Duration = man-hours ÷ (headcount × hours/day), stretched by weather &amp; site slowdowns. Compress on the Schedule page — manpower scales to hold the date.</p>
          {!readOnly && (
            <div className="flex items-center gap-2 shrink-0">
              {crew?.members?.length ? <button onClick={() => doSave(true)} data-testid={`crew-clear-${group}`} className="text-[11px] text-rose-500 hover:text-rose-700">Remove crew</button> : null}
              {onSaveToLibrary && <button onClick={() => { const c = buildCrew(); if (!c.members.length) { toast.error("Add at least one crew member first"); return; } const nm = window.prompt("Save this crew to your library as:", `${group} crew`); if (nm) onSaveToLibrary(nm, c); }} data-testid={`crew-save-lib-${group}`} className="text-[11px] text-[#003E87] hover:underline">Save to library</button>}
              <button onClick={() => doSave(false)} disabled={blocked} data-testid={`crew-save-${group}`} className="text-xs font-medium bg-[#003E87] hover:bg-[#00306a] text-white rounded-lg px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed">Save crew &amp; equipment</button>
            </div>
          )}
        </div>
      </div>
    </DraggablePanel>
  );
}
